// Keeps the grouped/individual toggle at the same viewport position when switching views.
// Switching reloads the data and the page briefly collapses to loading spinners, which would
// otherwise clamp the scroll position back towards the top.

const ANCHOR_DURATION_MS = 4000;
const USER_INPUT_EVENTS = ['wheel', 'touchstart', 'keydown', 'pointerdown'];

// Set by the toggle that was clicked and held until the deadline or until the user scrolls, so a
// toggle that mounts late (or mounts more than once while loading) can still pick it up. Kept in
// memory rather than history state so Back/Forward and reloads do not replay it.
let pending: {
    id: number,
    tab?: string,
    viewportTop: number,
    scroller: HTMLElement,
    expectedScrollTop: number,
    restore: () => void,
    timer: number,
} | null = null;
let nextId = 1;

// Resolved at click time, while the page is still full height, and reused for the new view.
const getScroller = (element: HTMLElement): HTMLElement => {
    for (let node = element.parentElement; node && node !== document.body; node = node.parentElement) {
        const { overflowY } = window.getComputedStyle(node);
        if ((overflowY === 'auto' || overflowY === 'scroll') && node.scrollHeight > node.clientHeight) {
            return node;
        }
    }
    return (document.scrollingElement || document.documentElement) as HTMLElement;
};

const isUserScroll = (id: number) => pending?.id === id && Math.abs(pending.scroller.scrollTop - pending.expectedScrollTop) > 2;

const endPending = (id: number) => {
    if (pending?.id !== id) return; // a newer switch owns it
    window.clearTimeout(pending.timer);
    pending.restore();
    pending = null;
};

// Called just before navigating: stops the page shrinking (and the browser's own scroll
// anchoring from shifting it) while the new view loads. Always expires on its own.
export const lockScrollHeight = (toggle: HTMLElement | null, tab?: string) => {
    if (!toggle) return;
    if (pending) endPending(pending.id);
    const scroller = getScroller(toggle);
    const isWindow = scroller === document.scrollingElement || scroller === document.documentElement;
    const content = (isWindow ? document.body : scroller.firstElementChild || scroller) as HTMLElement;
    const previous = { minHeight: content.style.minHeight, overflowAnchor: scroller.style.overflowAnchor };
    const id = nextId++;
    pending = {
        id,
        tab,
        viewportTop: toggle.getBoundingClientRect().top,
        scroller,
        expectedScrollTop: scroller.scrollTop,
        restore: () => {
            content.style.minHeight = previous.minHeight;
            scroller.style.overflowAnchor = previous.overflowAnchor;
        },
        timer: window.setTimeout(() => endPending(id), ANCHOR_DURATION_MS),
    };
    content.style.minHeight = `${content.scrollHeight}px`;
    scroller.style.overflowAnchor = 'none';
};

// Called whenever a toggle mounts or the location changes: if a switch is pending for this
// toggle's tab, holds it at its previous viewport position while the content above it loads.
// The returned cleanup only stops this toggle's loop; the lock ends on its deadline or user input.
export const anchorPendingToggle = (toggle: HTMLElement | null, tab?: string): (() => void) | undefined => {
    if (!toggle || !pending || pending.tab !== tab) return;
    const { id, scroller } = pending;
    const scrollTarget: HTMLElement | Window = scroller === document.scrollingElement ? window : scroller;
    let frame = 0;

    const stopLoop = () => {
        cancelAnimationFrame(frame);
        USER_INPUT_EVENTS.forEach(type => window.removeEventListener(type, onUserInput, true));
        scrollTarget.removeEventListener('scroll', onScroll);
    };
    const onUserInput = () => {
        stopLoop();
        endPending(id);
    };
    // Catches scrolling that fires no input event, such as dragging the scrollbar.
    const onScroll = () => {
        if (isUserScroll(id)) onUserInput();
    };

    const align = () => {
        if (pending?.id !== id) {
            stopLoop();
            return;
        }
        // Skip while the toggle is hidden (e.g. its tab pane is not shown yet).
        if (toggle.offsetParent !== null) {
            const delta = toggle.getBoundingClientRect().top - pending.viewportTop;
            if (Math.abs(delta) > 1) {
                scroller.scrollTop += delta;
                pending.expectedScrollTop = scroller.scrollTop;
            }
        }
        frame = requestAnimationFrame(align);
    };

    USER_INPUT_EVENTS.forEach(type => window.addEventListener(type, onUserInput, { capture: true, passive: true }));
    scrollTarget.addEventListener('scroll', onScroll, { passive: true });
    align();
    return stopLoop;
};
